import { CPLAT_API_ROUTES, usePost } from '@/api';
import { default as EyeOffIcon } from '@/assets/icons/EyeOffIcon.svg?react';
import { default as EyeOpenIcon } from '@/assets/icons/EyeOpenIcon.svg?react';
import GreenCheckIcon from '@/assets/icons/GreenCheckIcon.svg?react';
import { Button } from '@/components/atoms/NewPcButton';
import Input from '@/components/V3/Input';
import { EmailDomain } from '@/constants/email-domain';
import { ROUTES } from '@/routes';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { ChevronRightIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import z from 'zod';
import { SignupTerms } from './signup-terms';
import { cn } from '@/utils/tailwind';
import { SIGNUP_TERMS } from '@/constants/signup-terms';
import { addHyphenPhoneNumber, removeHyphen } from '@/utils/phone';
import { useLogEvent } from '@/hooks/use-log-event';
import { GA_EVENT_NAMES, GA_ROUTES } from '@/constants/ga';
import {
  createPhoneVerificationRequestKey,
  shouldRequestPhoneVerification,
} from './phone-verification-utils';

const formSchema = z
  .object({
    phone: z.string(),
    verifyCode: z.string(),
    email: z.email('유효하지 않은 이메일 형식입니다'),
    password: z
      .string()
      .min(8, { message: '영문, 숫자, 특수문자를 포함한 8~20자리' })
      .max(20, { message: '영문, 숫자, 특수문자를 포함한 8~20자리' })
      .regex(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{};:,<.>]).{8,20}$/, {
        message: '영문, 숫자, 특수문자를 포함한 8~20자리',
      }),
    confirm_password: z.string(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: '비밀번호가 일치하지 않습니다.',
    path: ['confirm_password'],
  });

type FormType = z.infer<typeof formSchema>;

type SignupFormProps = {
  compact?: boolean;
  signinPath?: string;
  successRedirectPath?: string;
};

export const SignupForm = ({
  compact = false,
  signinPath = ROUTES.signin,
  successRedirectPath = ROUTES.root,
}: SignupFormProps) => {
  const navigate = useNavigate();

  const { logEvent } = useLogEvent();

  const [checkedItems, setCheckedItems] = useState<boolean[]>(
    new Array(SIGNUP_TERMS.length).fill(false),
  );
  const allChecked = checkedItems.every(Boolean);
  const requiredChecked = SIGNUP_TERMS.every((t, i) => t.isOptional || checkedItems[i]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [buttonLabel, setButtonLabel] = useState('인증번호 발송');
  const TOTAL_SECONDS = 3 * 60; // 3분 = 180초
  const [remaining, setRemaining] = useState(TOTAL_SECONDS);
  const endTimeRef = useRef<number | null>(null);
  const [smsToken, setSmsToken] = useState<string>();
  const lastVerificationRequestKeyRef = useRef<string | null>(null);
  const [, setCplatToken] = useLocalStorage('cplatToken', '');

  const [codeVerified, setCodeVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);

  const [verificationToken, setVerificationToken] = useState<string>();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const rafRef = useRef<number | null>(null);

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
  } = useForm<FormType>({ resolver: zodResolver(formSchema) });

  const watchedPhone = watch('phone');
  const watchedVerifyCode = watch('verifyCode');
  const watchedEmail = watch('email');
  const watchedPassword = watch('password');
  const watchedConfirmPassword = watch('confirm_password');
  const compactInputClassName = compact
    ? 'h-11 rounded-sm border-gr-300 px-2.5 py-3 text-gr-800 Body5M14 placeholder:text-gr-400 placeholder:text-[14px] placeholder:font-medium placeholder:leading-[22px]'
    : undefined;
  const compactVerificationInputClassName = compact
    ? 'h-10 rounded-sm border-gr-300 px-2.5 py-[9px] pr-10 text-gr-800 Body5M14 placeholder:text-gr-400 placeholder:text-[14px] placeholder:font-medium placeholder:leading-[22px]'
    : undefined;
  const compactActionButtonClassName = compact ? 'h-11 shrink-0 px-2.5 Body5M14' : undefined;
  const compactDisabledButtonClassName = compact ? 'disabled:bg-gr-200 disabled:text-wh' : undefined;

  const { mutate: onSendVerifyCode } = usePost(
    CPLAT_API_ROUTES.sendAuthenticationCode,
    undefined,
    undefined,
    true,
  );
  const { mutate: onSignup } = usePost(CPLAT_API_ROUTES.signup, undefined, undefined, true);

  const { mutate: onVerifyCode } = usePost(
    CPLAT_API_ROUTES.verifyAuthenticationCode,
    undefined,
    undefined,
    true,
  );

  const { mutate: onVerifyEmail } = usePost(
    CPLAT_API_ROUTES.verifyEmail,
    undefined,
    undefined,
    true,
  );

  const update = useCallback(() => {
    if (!endTimeRef.current) return;

    const now = Date.now();
    const diff = Math.max(0, Math.ceil((endTimeRef.current - now) / 1000));

    setRemaining(diff);

    if (diff <= 0) {
      endTimeRef.current = null;
      cancelAnimationFrame(rafRef.current!);
      return;
    }

    rafRef.current = requestAnimationFrame(update);
  }, []);

  const restart = () => {
    cancelAnimationFrame(rafRef.current!);
    endTimeRef.current = Date.now() + TOTAL_SECONDS * 1000;
    setRemaining(TOTAL_SECONDS);
    rafRef.current = requestAnimationFrame(update);
  };
  const { mutate: onCheck } = usePost(CPLAT_API_ROUTES.checkPhone, undefined, undefined, true);

  const handleCheckPhone = () => {
    onCheck(
      { phoneNo: removeHyphen(watchedPhone) },
      {
        onSuccess: (response) => {
          const isRegistered = response.data.data[0].isRegistered;
          if (isRegistered) setError('phone', { message: '이미 사용중인 휴대폰 번호입니다.' });
          else handleSendVerifyCode();
        },
      },
    );
  };

  const handleSendVerifyCode = () => {
    onSendVerifyCode(
      { phoneNo: removeHyphen(watchedPhone) },
      {
        onSuccess: (response) => {
          clearErrors();

          setButtonLabel('재발송');
          restart();
          setSmsToken(response.data.data[0].smsToken);
        },
      },
    );
  };

  useEffect(() => {
    const requestKey = createPhoneVerificationRequestKey(smsToken, watchedVerifyCode);

    if (!requestKey) {
      if ((watchedVerifyCode?.length ?? 0) < 6) {
        lastVerificationRequestKeyRef.current = null;
      }
      return;
    }

    if (
      !shouldRequestPhoneVerification({
        requestKey,
        lastRequestKey: lastVerificationRequestKeyRef.current,
      })
    ) {
      return;
    }

    lastVerificationRequestKeyRef.current = requestKey;

    onVerifyCode(
      { smsToken, smsCode: watchedVerifyCode },
      {
        onSuccess: (response) => {
          const verificationToken = response.data.data[0].verificationToken;
          setVerificationToken(verificationToken);
          setCodeVerified(true);
          clearErrors();
        },
        onError: () => {
          setError('verifyCode', { message: '인증번호가 올바르지 않습니다' });
        },
      },
    );
  }, [clearErrors, onVerifyCode, setError, smsToken, watchedVerifyCode]);

  useEffect(() => {
    if (remaining === 0) setError('verifyCode', { message: '입력시간이 초과되었습니다' });
  }, [remaining, setError]);

  // 휴대폰 번호가 바뀌면 인증 상태 초기화
  useEffect(() => {
    setButtonLabel('인증번호 발송');
    setCodeVerified(false);
    setValue('verifyCode', '');
  }, [setValue, watchedPhone]);

  useEffect(() => {
    setPasswordVerified(false);
  }, [watchedPassword, watchedConfirmPassword]);

  useEffect(() => {
    setEmailVerified(false);
  }, [watchedEmail]);

  useEffect(() => {
    if (!watchedPassword) return;
    if (watchedPassword === watchedConfirmPassword) setPasswordVerified(true);
  }, [watchedPassword, watchedConfirmPassword]);

  const handlePhoneNoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/[^\d-]/g, '');
    const digits = removeHyphen(value);

    if (digits.length > 11) return;

    const isnNotIncludePrefixNum = digits.length >= 3 && digits.substring(0, 3) !== '010';

    if (isnNotIncludePrefixNum) {
      setError('phone', {
        type: 'pattern',
        message: '휴대폰 번호는 "010-" 으로 시작해야합니다.',
      });
    } else {
      setError('phone', {});
    }

    value = addHyphenPhoneNumber(digits);

    setValue('phone', value);
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center bg-white text-left',
        compact ? 'w-full gap-5' : 'w-[480px] gap-8 rounded-[20px] p-10 max-md:w-full max-md:p-5',
      )}
    >
      <div className="w-full space-y-5">
        <div className={cn('w-full', compact ? 'flex flex-col gap-3' : 'space-y-3')}>
          <div className={cn('flex flex-col', compact ? 'gap-3' : 'gap-2')}>
            <div className="flex flex-col gap-2">
              <span className="text-left text-gr-800 Caption1M12">휴대폰 번호</span>
              <div className="flex gap-2 ">
                <div className="min-w-0 flex-1">
                  <Input
                    placeholder="휴대폰 번호를 입력해 주세요"
                    {...register('phone')}
                    className={compactInputClassName}
                    Error={{
                      isError: !!errors.phone?.message,
                      errorMessage: errors.phone?.message,
                      component: (
                        <button
                          className="flex items-center py-1"
                          onClick={() => navigate(signinPath)}
                        >
                          <span className="Caption1M12">로그인하기</span>
                          <ChevronRightIcon width={16} height={16} />
                        </button>
                      ),
                    }}
                    onChange={handlePhoneNoChange}
                  />
                </div>
                <Button
                  variant={compact && buttonLabel === '재발송' ? 'secondary' : 'solid'}
                  className={cn(compactActionButtonClassName, compactDisabledButtonClassName)}
                  onClick={handleCheckPhone}
                  disabled={!watchedPhone}
                >
                  {buttonLabel}
                </Button>
              </div>
            </div>
            {buttonLabel === '재발송' && (
              <div className="flex flex-col items-start gap-2">
                <span className="text-gr-800 Caption1M12">인증번호</span>
                <Input
                  disabled={codeVerified}
                  placeholder="인증번호를 입력해 주세요"
                  className={cn(compact ? compactVerificationInputClassName : compactInputClassName)}
                  InputProps={{
                    endAdornment: codeVerified ? (
                      <GreenCheckIcon />
                    ) : (
                      <span className={cn({ 'text-red-400': remaining === 0 })}>
                        {formatTime(remaining)}
                      </span>
                    ),
                    endAdornmentStyle: 'text-blue-400 Body5M14 right-2',
                  }}
                  {...register('verifyCode')}
                  Error={{
                    isError: !!errors.verifyCode?.message,
                    errorMessage: errors.verifyCode?.message,
                  }}
                  maxLength={6}
                  autoComplete="off"
                />
              </div>
            )}
          </div>
          {/* ✅ 이메일 입력 영역 (자동완성 도메인 드롭다운 포함) */}
          <div className="flex flex-col items-start gap-2">
            <span className="text-gr-800 Caption1M12">이메일</span>
            <div className="flex w-full gap-2">
              <div className="relative min-w-0 flex-1">
                <Input
                  placeholder="이메일 주소를 입력해 주세요"
                  {...register('email')}
                  className={cn('w-full', compactInputClassName)}
                  onChange={(e) => {
                    setValue('email', e.target.value);
                  }}
                  type="email"
                  spellCheck={false}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  Error={{
                    isError: !!errors.email?.message,
                    errorMessage: errors.email?.message,
                    component: (
                      <button
                        className="flex items-center py-1"
                        onClick={() => navigate(signinPath)}
                      >
                        <span className="Caption1M12">로그인하기</span>
                        <ChevronRightIcon width={16} height={16} />
                      </button>
                    ),
                  }}
                  InputProps={{ endAdornment: emailVerified && <GreenCheckIcon /> }}
                />

                {showSuggestions && watchedEmail && !watchedEmail.includes('@') && (
                  <div className="absolute z-10 mt-1 w-full space-y-2 overflow-hidden rounded-xl border border-gr-200 bg-white p-3 shadow-md">
                    {EmailDomain.map((domain) => (
                      <button
                        key={domain}
                        type="button"
                        onClick={() => {
                          setValue('email', `${watchedEmail}@${domain}`);
                          setShowSuggestions(false);
                        }}
                        className="w-full rounded-sm p-1  text-left hover:bg-gr-100"
                      >
                        <span className="text-gr-800 Body4S14">{watchedEmail}</span>
                        <span className="text-gr-600 Body5M14">@{domain}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {!emailVerified && (
                <Button
                  className={cn(compactActionButtonClassName, compactDisabledButtonClassName)}
                  disabled={!watchedEmail || !!errors.email?.message}
                  onClick={() => {
                    onVerifyEmail(
                      { accountEmail: watchedEmail },
                      {
                        onSuccess: (response) => {
                          const isRegistered = response.data.data[0].isRegistered;
                          if (isRegistered)
                            setError('email', { message: '이미 사용중인 이메일 입니다' });
                          else {
                            setEmailVerified(true);
                            clearErrors('email');
                          }
                        },
                      },
                    );
                  }}
                >
                  중복확인
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-col items-start gap-2">
            <span className="text-gr-800 Caption1M12">비밀번호</span>
            <Input
              placeholder="영문,숫자,특수문자를 포함한 8~20자리"
              {...register('password')}
              className={compactInputClassName}
              autoComplete="new-password"
              Error={{
                isError: !!errors.password?.message,
                errorMessage: errors.password?.message,
              }}
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: watchedPassword && (
                  <button onClick={() => setShowPassword((prev) => !prev)}>
                    {showPassword ? (
                      <EyeOpenIcon className="text-gr-400" />
                    ) : (
                      <EyeOffIcon className="text-gr-400" />
                    )}
                  </button>
                ),
                endAdornmentStyle: 'top-6 right-2.5',
              }}
            />
          </div>
          <div className="flex flex-col items-start gap-2">
            <Input
              placeholder="비밀번호를 한번 더 입력해 주세요"
              {...register('confirm_password')}
              className={compactInputClassName}
              Error={{
                isError: !!errors.confirm_password?.message,
                errorMessage: errors.confirm_password?.message,
              }}
              type={showConfirmPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: passwordVerified ? (
                  <GreenCheckIcon />
                ) : (
                  watchedConfirmPassword && (
                    <button onClick={() => setShowConfirmPassword((prev) => !prev)}>
                      {showConfirmPassword ? (
                        <EyeOpenIcon className="text-gr-400" />
                      ) : (
                        <EyeOffIcon className="text-gr-400" />
                      )}
                    </button>
                  )
                ),
                endAdornmentStyle: 'top-6 right-2.5',
              }}
            />
          </div>
        </div>
        <SignupTerms
          checkedItems={checkedItems}
          handleAllCheck={() => {
            const newChecked = new Array(SIGNUP_TERMS.length).fill(!allChecked);
            setCheckedItems(newChecked);
          }}
          handleSingleCheck={(index: number) => {
            const newChecked = [...checkedItems];
            newChecked[index] = !newChecked[index];
            setCheckedItems(newChecked);
          }}
          compact={compact}
        />
      </div>
      <Button
        className={cn('w-full', compact && 'h-11 rounded-sm disabled:bg-gr-200 Body3S15')}
        onClick={handleSubmit((values) =>
          onSignup(
            {
              verificationToken,
              accountEmail: values.email,
              plainPassword: values.password,
              adAgreeYn: allChecked ? 'Y' : 'N',
              SignUpPlatform: 'sellerbox',
            },
            {
              onSuccess: (response) => {
                const cplatToken = response.data.data[0].cplatToken;
                setCplatToken(cplatToken);
                navigate(successRedirectPath, { replace: true });
                logEvent({
                  eventName: GA_EVENT_NAMES.signup.success,
                  pageTitle: GA_ROUTES.signup,
                });
              },
              onError: (error) => {
                const axiosError = error as AxiosError;
                const errorMessage = (axiosError.response?.data as { message: string })?.message;

                setError('phone', { message: errorMessage });
              },
            },
          ),
        )}
        disabled={!emailVerified || !codeVerified || !passwordVerified || !requiredChecked}
      >
        회원가입
      </Button>
    </div>
  );
};

const formatTime = (seconds: number) => {
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};
